import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HouseholdProvider, useHousehold } from "@/context/HouseholdContext";
import { DemoProvider } from "@/context/DemoContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AcceptInvite from "@/pages/AcceptInvite";
import Layout from "@/components/Layout";
import AccountLayout from "@/components/AccountLayout";
import Portfolio from "@/pages/Portfolio";
import MorningCheck from "@/pages/MorningCheck";
import DigestSettings from "@/components/settings/DigestSettings";
import BackfillSummary from "@/components/settings/BackfillSummary";
import { api } from "@/lib/api";
import { useState, useEffect } from "react";
import RealEstate from "@/pages/RealEstate";
import OilGas from "@/pages/OilGas";
import MortgageNotes from "@/pages/MortgageNotes";
import PrivateFunds from "@/pages/PrivateFunds";
import PreciousMetals from "@/pages/PreciousMetals";
import Issues from "@/pages/Issues";
import AskMax from "@/pages/AskMax";
import NetWorth from "@/pages/NetWorth";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import Liabilities from "@/pages/Liabilities";
import Entities from "@/pages/Entities";
import EntityDetail from "@/pages/EntityDetail";
import Contacts from "@/pages/Contacts";
import Vault from "@/pages/Vault";
import Household from "@/pages/Household";
import Dashboard from "@/pages/Dashboard";
import Obligations from "@/pages/Obligations";
import Properties from "@/pages/Properties";
import PropertyManagers from "@/pages/PropertyManagers";
import PropertyManagerDetail from "@/pages/PropertyManagerDetail";
import Transactions from "@/pages/Transactions";
import Inbox from "@/pages/Inbox";
import Ingest from "@/pages/Ingest";
import BillReview from "@/pages/BillReview";
import Archive from "@/pages/Archive";
import CommsAudit from "@/pages/CommsAudit";
import Settings from "@/pages/Settings";
import Subscriptions from "@/pages/Subscriptions";
import { PlaidOauthReturn } from "@/components/PlaidOauthReturn";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-sm uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading…</div>
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireHousehold({ children }) {
  const { loading, households } = useHousehold();
  if (loading) return <Loader />;
  if (households.length === 0) return <Navigate to="/onboarding" replace />;
  return children;
}

// Landing: Morning Check is the default once a household has any Plaid Item OR uploaded Document.
function LandingRedirect() {
  const { hid } = useHousehold();
  const [dest, setDest] = useState(null);
  useEffect(() => {
    if (!hid) return;
    let cancel = false;
    api.get(`/ledger/trust-summary?household_id=${hid}`)
      .then((r) => {
        const s = r.data || {};
        const has = (s.apu_counts?.total || 0) > 0 || (s.plaid?.items_total || 0) > 0;
        if (!cancel) setDest(has ? "/morning-check" : "/portfolio");
      })
      .catch(() => { if (!cancel) setDest("/portfolio"); });
    return () => { cancel = true; };
  }, [hid]);
  if (!dest) return <Loader />;
  return <Navigate to={dest} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/accept-invite" element={<Protected><AcceptInvite /></Protected>} />
      <Route path="/plaid-oauth" element={<Protected><PlaidOauthReturn /></Protected>} />

      {/* Main app — Layout with full sidebar */}
      <Route
        element={
          <Protected>
            <RequireHousehold>
              <Layout />
            </RequireHousehold>
          </Protected>
        }
      >
        {/* Default landing: Morning Check when there's data, else Portfolio */}
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/morning-check" element={<MorningCheck />} />
        {/* Digest + backfill now live in the Account & Settings section — keep old links working */}
        <Route path="/admin/backfill-summary" element={<Navigate to="/settings/backfill" replace />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/networth" element={<Navigate to="/portfolio" replace />} />

        {/* Portfolio verticals */}
        <Route path="/real-estate"     element={<RealEstate />} />
        <Route path="/oil-gas"         element={<OilGas />} />
        <Route path="/mortgage-notes"  element={<MortgageNotes />} />
        <Route path="/private-funds"   element={<PrivateFunds />} />
        <Route path="/precious-metals" element={<PreciousMetals />} />

        {/* Operations */}
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/issues"       element={<Issues />} />
        <Route path="/documents"    element={<Navigate to="/vault" replace />} />
        <Route path="/ask-max"      element={<AskMax />} />

        {/* Legacy/internal detail & sub-pages (kept functional, redesigned later) */}
        <Route path="/assets"             element={<Assets />} />
        <Route path="/assets/:id"         element={<AssetDetail />} />
        <Route path="/liabilities"        element={<Liabilities />} />
        <Route path="/entities"           element={<Entities />} />
        <Route path="/entities/:id"       element={<EntityDetail />} />
        <Route path="/dashboard"          element={<Dashboard />} />
        <Route path="/obligations"        element={<Obligations />} />
        <Route path="/properties"         element={<Properties />} />
        <Route path="/properties/:id"     element={<AssetDetail />} />
        <Route path="/property-managers"  element={<PropertyManagers />} />
        <Route path="/property-managers/:id" element={<PropertyManagerDetail />} />
        <Route path="/ingest"             element={<Navigate to="/inbox" replace />} />
        <Route path="/inbox"              element={<Ingest />} />
        <Route path="/bills-inbox"        element={<Inbox />} />
        <Route path="/bills-inbox/:id"    element={<BillReview />} />
        <Route path="/archive"            element={<Archive />} />
        <Route path="/comms/audit"        element={<CommsAudit />} />
      </Route>

      {/* Account section — AccountLayout with settings sidebar */}
      <Route
        element={
          <Protected>
            <RequireHousehold>
              <AccountLayout />
            </RequireHousehold>
          </Protected>
        }
      >
        <Route path="/settings"          element={<Settings />} />
        <Route path="/settings/digest"   element={<DigestSettings />} />
        <Route path="/settings/backfill" element={<BackfillSummary />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/contacts"      element={<Contacts />} />
        <Route path="/vault"         element={<Vault />} />
        <Route path="/household"     element={<Household />} />
      </Route>

      <Route path="*" element={<Navigate to="/portfolio" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <HouseholdProvider>
            <DemoProvider>
              <AppRouter />
              <Toaster position="top-right" />
            </DemoProvider>
          </HouseholdProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
